import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft,
  User,
  BarChart3,
  Target,
  Trophy,
  Settings,
  Calendar,
  TrendingUp,
  DollarSign,
  Phone,
  Mail,
  MapPin,
  Edit,
  Save,
  X,
  Power,
  Trash2,
  Award,
  Star,
  Clock,
  Plus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Composant pour simplifier le rendu des événements
const EventSlot = ({ dayIndex, hourIndex, event }) => {
  if (!event) return null;
  
  const colorClasses = {
    blue: 'bg-blue-500 hover:bg-blue-600',
    emerald: 'bg-emerald-500 hover:bg-emerald-600',
    purple: 'bg-purple-500 hover:bg-purple-600',
    orange: 'bg-orange-500 hover:bg-orange-600'
  };
  
  return (
    <div className={`absolute inset-x-1 top-1 bottom-1 ${colorClasses[event.color] || 'bg-gray-500 hover:bg-gray-600'} text-white rounded text-xs p-1 z-10 cursor-pointer transition-colors`}>
      <div className="font-medium">{event.start} - {event.end}</div>
      <div className="truncate">{event.title}</div>
    </div>
  );
};

// Composant pour les événements dans le thème sombre avec style jaune/orange
const DarkEventSlot = ({ dayIndex, hourIndex, event }) => {
  if (!event) return null;
  
  return (
    <div className="absolute inset-x-1 top-1 bottom-1 bg-yellow-600 hover:bg-yellow-500 text-black rounded text-xs p-1 z-10 cursor-pointer transition-colors border border-yellow-500">
      <div className="font-semibold text-xs leading-tight">{event.title}</div>
      <div className="text-xs opacity-90 leading-tight">{event.time}</div>
      {event.location && (
        <div className="text-xs opacity-80 leading-tight truncate">{event.location}</div>
      )}
    </div>
  );
};

const CommercialProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [commercial, setCommercial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profil');
  const [filterPeriod, setFilterPeriod] = useState('mois');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const week = Math.ceil(((now - new Date(year, 0, 1)) / 86400000 + 1) / 7);
    return `${year}-W${week.toString().padStart(2, '0')}`;
  });
  const [selectedFilterMonth, setSelectedFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  
  // États pour la gestion des objectifs (admin)
  const [currentUser, setCurrentUser] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [isCreatingObjective, setIsCreatingObjective] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  
  // États pour la gestion mensuelle des objectifs
  const [currentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  const [monthlyObjectives, setMonthlyObjectives] = useState({});
  const [newObjective, setNewObjective] = useState({
    type: '',
    label: '',
    target: '',
    color: 'blue',
    icon: 'Target',
    month: ''
  });
  
  // États pour l'historique des performances
  const [performanceHistory, setPerformanceHistory] = useState([]);
  
  // États pour l'agenda
  const [agendaView, setAgendaView] = useState('semaine'); // 'semaine' ou 'mois'
  const [agendaDate, setAgendaDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  
  // États pour l'autocomplétion d'adresses
  const [addressInput, setAddressInput] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [events, setEvents] = useState([
    { id: 1, title: 'M. Martin - Visite', start: '09:00', end: '10:00', day: 0, type: 'visite', color: 'blue' },
    { id: 2, title: 'Mme Dubois - Appel', start: '14:00', end: '14:30', day: 1, type: 'tel', color: 'emerald' },
    { id: 3, title: 'SAS TechSol', start: '16:00', end: '17:00', day: 1, type: 'visite', color: 'blue' },
    { id: 4, title: 'M. Bernard - Signature', start: '10:00', end: '11:00', day: 2, type: 'signature', color: 'purple' },
    { id: 5, title: 'Famille Leroy', start: '09:30', end: '10:30', day: 4, type: 'visite', color: 'blue' },
    { id: 6, title: 'M. Petit', start: '15:00', end: '15:30', day: 4, type: 'tel', color: 'emerald' }
  ]);

  const tabs = [
    { id: 'profil', label: 'Profil', icon: User },
    { id: 'statistiques', label: 'Statistiques', icon: BarChart3 },
    { id: 'objectifs', label: 'Objectifs', icon: Target },
    { id: 'performance', label: 'Performance', icon: Trophy },
    { id: 'planning', label: 'Planning', icon: Calendar },
    { id: 'parametres', label: 'Paramètres', icon: Settings }
  ];

  useEffect(() => {
    fetchCommercial();
    fetchCurrentUser();
    fetchMonthlyObjectives();
    fetchPerformanceHistory();
  }, [id]);

  useEffect(() => {
    // Recharger l'historique quand les objectifs mensuels changent
    if (Object.keys(monthlyObjectives).length > 0) {
      fetchPerformanceHistory();
    }
  }, [monthlyObjectives]);

  const fetchCurrentUser = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      setCurrentUser(userData);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'utilisateur:', error);
    }
  };

  const fetchMonthlyObjectives = async () => {
    try {
      // Simulation d'objectifs mensuels - à remplacer par un appel API
      const now = new Date();
      const objectivesByMonth = {};
      
      // Générer seulement la structure des mois sans objectifs par défaut
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        
        // Initialiser avec un tableau vide pour chaque mois
        objectivesByMonth[monthKey] = [];
      }
      
      setMonthlyObjectives(objectivesByMonth);
      
      // Initialiser les objectifs du mois actuel (vide au début)
      setObjectives([]);
      
    } catch (error) {
      console.error('Erreur lors du chargement des objectifs mensuels:', error);
    }
  };

  const addObjectiveToMonth = () => {
    if (newObjective.type && newObjective.label && newObjective.target && newObjective.month) {
      const objective = {
        id: `${newObjective.month}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        ...newObjective,
        target: parseInt(newObjective.target),
        current: 0
      };
      
      const updatedMonthlyObjectives = {
        ...monthlyObjectives,
        [newObjective.month]: [...(monthlyObjectives[newObjective.month] || []), objective]
      };
      
      setMonthlyObjectives(updatedMonthlyObjectives);
      
      // Si c'est le mois actuel, mettre à jour la vue
      if (newObjective.month === currentMonth) {
        setObjectives(updatedMonthlyObjectives[currentMonth]);
      }
      
      // Fermer le formulaire et réinitialiser
      setIsCreatingObjective(false);
      setNewObjective({
        type: '',
        label: '',
        target: '',
        color: 'blue',
        icon: 'Target',
        month: ''
      });
    }
  };

  const updateObjectiveInMonth = (id, field, value) => {
    // Trouver dans quel mois se trouve cet objectif
    let targetMonth = null;
    Object.keys(monthlyObjectives).forEach(monthKey => {
      if (monthlyObjectives[monthKey]?.some(obj => obj.id === id)) {
        targetMonth = monthKey;
      }
    });

    if (targetMonth) {
      const updatedMonthlyObjectives = {
        ...monthlyObjectives,
        [targetMonth]: monthlyObjectives[targetMonth]?.map(obj => 
          obj.id === id ? { ...obj, [field]: value } : obj
        ) || []
      };
      
      setMonthlyObjectives(updatedMonthlyObjectives);
      
      // Si c'est le mois actuel, mettre à jour la vue
      if (targetMonth === currentMonth) {
        setObjectives(updatedMonthlyObjectives[targetMonth]);
      }
    }
  };

  const deleteObjectiveFromMonth = (id) => {
    // Trouver dans quel mois se trouve cet objectif
    let targetMonth = null;
    Object.keys(monthlyObjectives).forEach(monthKey => {
      if (monthlyObjectives[monthKey]?.some(obj => obj.id === id)) {
        targetMonth = monthKey;
      }
    });

    if (targetMonth) {
      const updatedMonthlyObjectives = {
        ...monthlyObjectives,
        [targetMonth]: monthlyObjectives[targetMonth]?.filter(obj => obj.id !== id) || []
      };
      
      setMonthlyObjectives(updatedMonthlyObjectives);
      
      // Si c'est le mois actuel, mettre à jour la vue
      if (targetMonth === currentMonth) {
        setObjectives(updatedMonthlyObjectives[targetMonth]);
      }
    }
  };

  const getMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      .charAt(0).toUpperCase() + date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).slice(1);
  };

  const fetchPerformanceHistory = async () => {
    try {
      // Génération d'historique sur 6 mois avec les 4 nouveaux types
      const months = [];
      const currentDate = new Date();
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const monthName = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        
        // Récupérer les objectifs définis pour ce mois spécifique
        const monthObjectives = monthlyObjectives[monthKey] || [];
        
        // Simulation de données réalistes avec variation
        const baseProspects = 40 + Math.floor(Math.random() * 20);
        const baseProjects = Math.floor(baseProspects * (0.4 + Math.random() * 0.3));
        const baseInstallations = Math.floor(baseProjects * (0.6 + Math.random() * 0.3));
        const conversionRate = Math.round((baseInstallations / baseProspects) * 100);
        
        months.push({
          id: monthKey,
          mois: monthName.charAt(0).toUpperCase() + monthName.slice(1),
          prospects: {
            objectif: monthObjectives.find(obj => obj.type === 'prospects')?.target || 50,
            realise: baseProspects
          },
          projects: {
            objectif: monthObjectives.find(obj => obj.type === 'projects')?.target || 25,
            realise: baseProjects
          },
          installations: {
            objectif: monthObjectives.find(obj => obj.type === 'installations')?.target || 20,
            realise: baseInstallations
          },
          conversion: {
            objectif: monthObjectives.find(obj => obj.type === 'conversion')?.target || 45,
            realise: conversionRate
          }
        });
      }
      
      setPerformanceHistory(months);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
    }
  };

  const updateHistoryValue = (monthId, type, field, value) => {
    setPerformanceHistory(prev => prev.map(month => 
      month.id === monthId 
        ? {
            ...month,
            [type]: {
              ...month[type],
              [field]: parseInt(value) || 0
            }
          }
        : month
    ));
  };

  const getMonthStatus = (month) => {
    const totalObjectifs = Object.keys(month).filter(key => key !== 'id' && key !== 'mois').length;
    let objectifsAtteints = 0;

    ['prospects', 'projects', 'installations', 'conversion'].forEach(type => {
      if (month[type] && month[type].realise >= month[type].objectif) {
        objectifsAtteints++;
      }
    });

    const pourcentage = (objectifsAtteints / totalObjectifs) * 100;
    
    if (pourcentage >= 75) return { label: 'Excellent mois', color: 'emerald' };
    if (pourcentage >= 50) return { label: 'Objectifs atteints', color: 'blue' };
    if (pourcentage >= 25) return { label: 'Proche objectifs', color: 'amber' };
    return { label: 'Objectifs non atteints', color: 'red' };
  };


  const fetchCommercial = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const commercialData = response.data.find(user => user.id === id);
      if (commercialData) {
        setCommercial(commercialData);
        setEditFormData({
          nom: commercialData.nom,
          prenom: commercialData.prenom,
          email: commercialData.email,
          telephone: commercialData.telephone || '',
          role: commercialData.role,
          actif: commercialData.actif,
          adresse: commercialData.adresse || '',
          code_postal: commercialData.code_postal || '',
          email_perso: commercialData.email_perso || ''
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement du commercial:', error);
    } finally {
      setLoading(false);
    }
  };

  const addObjective = addObjectiveToMonth;

  const updateObjective = updateObjectiveInMonth;

  const deleteObjective = deleteObjectiveFromMonth;

  const getIconComponent = (iconName) => {
    const icons = {
      Target,
      Phone,
      DollarSign,
      Award,
      Trophy,
      TrendingUp,
      BarChart3,
      Star,
      Clock,
      Plus
    };
    return icons[iconName] || Target;
  };

  const isAdmin = currentUser?.role === 'admin';

  const getRoleLabel = (role) => {
    switch (role) {
      case 'manager_vente':
        return 'Resp. Vente';
      case 'commercial':
        return 'Commercial';
      case 'employe_vente':
        return 'Commercial';
      default:
        return role;
    }
  };

  // Fonction pour obtenir les données selon la période et date sélectionnées
  const getPerformanceData = (period) => {
    // Simulation de données réalistes selon la période sélectionnée
    const generateData = (base, variation = 0.2) => {
      const factor = 1 + (Math.random() - 0.5) * variation;
      return Math.round(base * factor);
    };

    const baseData = {
      'jour': {
        base: { reçus: 3, contactés: 2, signés: 1, installés: 0 }
      },
      'semaine': {
        base: { reçus: 12, contactés: 10, signés: 6, installés: 4 }
      },
      'mois': {
        base: { reçus: 42, contactés: 38, signés: 23, installés: 18 }
      },
      'annee': {
        base: { reçus: 485, contactés: 420, signés: 267, installés: 198 }
      }
    };

    const base = baseData[period].base;
    const data = {
      reçus: generateData(base.reçus),
      contactés: generateData(base.contactés),
      signés: generateData(base.signés),
      installés: generateData(base.installés)
    };

    // Calcul du taux de transformation
    data.transfo = data.reçus > 0 ? Math.round((data.installés / data.reçus) * 100) : 0;

    // Labels selon la période
    const labels = {
      'jour': `le ${new Date(selectedDate).toLocaleDateString('fr-FR')}`,
      'semaine': `semaine ${selectedWeek.split('-W')[1]} de ${selectedWeek.split('-W')[0]}`,
      'mois': `${new Date(selectedMonth + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`,
      'annee': `année ${selectedYear}`
    };

    data.label = labels[period];
    data.trend = period === 'jour' ? '+2 vs hier' : 
                 period === 'semaine' ? '+15% vs semaine précédente' :
                 period === 'mois' ? '+8% vs mois précédent' :
                 '+12% vs année précédente';

    return data;
  };

  const currentData = getPerformanceData(filterPeriod);

  const getPerformanceBadge = (commercial) => {
    const performanceData = {
      'commercial@solaire.fr': { sales: 25, rank: 1, score: 95 },
      'manager.vente@solaire.fr': { sales: 20, rank: 2, score: 85 },
      'test.user.135606@example.com': { sales: 15, rank: 3, score: 75 },
      'test.user.170120@example.com': { sales: 10, rank: 4, score: 65 },
      'test.user.164346@example.com': { sales: 5, rank: 5, score: 45 }
    };

    // Fonction pour générer un score stable basé sur l'ID ou email
    const generateStableScore = (identifier) => {
      let hash = 0;
      for (let i = 0; i < identifier.length; i++) {
        const char = identifier.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      // Générer un score entre 25 et 95 basé sur le hash
      return Math.abs(hash % 71) + 25;
    };

    const perf = performanceData[commercial.email] || { 
      sales: Math.abs((commercial.id || commercial.email).split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % 20 + 1, 
      rank: Math.abs((commercial.id || commercial.email).split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % 10 + 1, 
      score: generateStableScore(commercial.id || commercial.email)
    };
    
    if (perf.score >= 90) {
      return { icon: '💎', label: 'Diamant', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' };
    } else if (perf.score >= 80) {
      return { icon: '🥇', label: 'Or', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
    } else if (perf.score >= 70) {
      return { icon: '🥈', label: 'Argent', color: 'bg-gray-100 text-gray-800 border-gray-300' };
    } else if (perf.score >= 60) {
      return { icon: '🥉', label: 'Bronze', color: 'bg-orange-100 text-orange-800 border-orange-300' };
    } else if (perf.score >= 40) {
      return { icon: '🧭', label: 'Explorer', color: 'bg-green-100 text-green-800 border-green-300' };
    } else {
      return { icon: '🌱', label: 'Débutant', color: 'bg-blue-100 text-blue-800 border-blue-300' };
    }
  };

  const handleToggleActive = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/users/${id}`, 
        { actif: !commercial.actif },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setCommercial(prev => ({ ...prev, actif: !prev.actif }));
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/users/${id}`, editFormData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCommercial(prev => ({ ...prev, ...editFormData }));
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      navigate('/commerciaux');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  // Fonctions pour l'agenda
  const navigateMonth = (direction) => {
    const newDate = new Date(agendaDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setAgendaDate(newDate);
  };

  const goToToday = () => {
    setAgendaDate(new Date());
  };

  const switchView = (view) => {
    setAgendaView(view);
  };

  const handleTimeSlotClick = (dayIndex, hourIndex) => {
    const hour = 8 + hourIndex;
    setSelectedTimeSlot({
      day: dayIndex,
      hour: hour,
      date: getWeekDays()[dayIndex]
    });
    setShowEventModal(true);
  };

  const getWeekDays = () => {
    const startOfWeek = new Date(agendaDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Lundi = début de semaine
    startOfWeek.setDate(diff);
    
    const weekDays = [];
    // Seulement du lundi au vendredi (5 jours)
    for (let i = 0; i < 5; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDays.push(date);
    }
    return weekDays;
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getEventForTimeSlot = (dayIndex, hourIndex) => {
    const hour = 8 + hourIndex;
    return events.find(event => {
      const eventHour = parseInt(event.start.split(':')[0]);
      return event.day === dayIndex && eventHour === hour;
    });
  };

  // Événements pour le thème sombre (jours ouvrables seulement)
  const darkEvents = [
    {
      id: 1,
      title: 'VISITE PERSONNE DE TULLE-SAS • PAC S/O',
      subtitle: 'AVEC RAPHAEL',
      time: 'Du 08:30 à 16:30',
      location: 'Près du 30111 de Gerville, 54600 Bruyèelles, France',
      day: 0, // Lundi (index 0 dans notre vue 5 jours)
      startHour: 8,
      duration: 8
    },
    {
      id: 2,
      title: 'VISITE PERSONNE DE J2',
      subtitle: 'AVEC 08:30 à 15:30',
      time: 'Près de Géen de Gambe, 54600 Bruyèelles, France',
      day: 1, // Mardi
      startHour: 8,
      duration: 7
    },
    {
      id: 3,
      title: 'CIRCULOIR 10 TULLE SE AMIES PAC 10000',
      subtitle: 'Du 08:30 à 16:30',
      time: 'à Près de Châtelain, 10370 Charmey, France',
      day: 2, // Mercredi
      startHour: 8,
      duration: 8
    },
    {
      id: 4,
      title: 'HAMAD 21 TUILLE PAC • HE • SAS',
      subtitle: 'Du 08:30 à 15:30',
      time: 'à Près Thermomètre, 20000 Dijon, France',
      day: 3, // Jeudi
      startHour: 8,
      duration: 7
    },
    {
      id: 5,
      title: 'HAMAD 21,2',
      subtitle: 'Du 08:30 à 15:30',
      time: 'à Près Thermomètre, 20000 Dijon, France',
      day: 4, // Vendredi
      startHour: 8,
      duration: 7
    },
    {
      id: 6,
      title: 'SAV FRAT/HAM HEL/A 50',
      subtitle: 'Du 13:30 à 15:30',
      time: '1 Rue George Sand 59140 Wormeries-Vaucelles',
      day: 4, // Vendredi (après-midi)
      startHour: 13,
      duration: 2
    },
    // Événements supplémentaires pour remplir la semaine
    {
      id: 7,
      title: 'FORMATION ÉQUIPE',
      subtitle: 'Du 09:00 à 12:00',
      time: 'Siège social - Salle de formation',
      day: 0, // Lundi matin
      startHour: 9,
      duration: 3
    },
    {
      id: 8,
      title: 'RÉUNION COMMERCIALE',
      subtitle: 'Du 14:00 à 16:00',
      time: 'Visioconférence',
      day: 2, // Mercredi après-midi
      startHour: 14,
      duration: 2
    }
  ];

  const getDarkEventForTimeSlot = (dayIndex, hourIndex) => {
    const hour = 4 + hourIndex; // Commence à 4h
    return darkEvents.find(event => {
      return event.day === dayIndex && 
             hour >= event.startHour && 
             hour < event.startHour + event.duration;
    });
  };

  const createNewEvent = (title, type = 'visite') => {
    if (!selectedTimeSlot || !title.trim()) return;
    
    const newEvent = {
      id: Date.now(),
      title: title.trim(),
      start: `${selectedTimeSlot.hour.toString().padStart(2, '0')}:00`,
      end: `${(selectedTimeSlot.hour + 1).toString().padStart(2, '0')}:00`,
      day: selectedTimeSlot.day,
      type: type,
      color: type === 'visite' ? 'blue' : type === 'tel' ? 'emerald' : 'purple',
      location: selectedAddress // Ajouter l'adresse sélectionnée
    };
    
    setEvents(prev => [...prev, newEvent]);
    setShowEventModal(false);
    setSelectedTimeSlot(null);
    // Réinitialiser les champs d'adresse
    setAddressInput('');
    setSelectedAddress('');
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
  };

  // Fonction pour rechercher des adresses avec l'API française
  const searchAddresses = async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`
      );
      const data = await response.json();
      
      if (data.features) {
        const suggestions = data.features.map(feature => ({
          label: feature.properties.label,
          address: feature.properties.label,
          coordinates: feature.geometry.coordinates,
          city: feature.properties.city,
          postcode: feature.properties.postcode
        }));
        
        setAddressSuggestions(suggestions);
        setShowAddressSuggestions(true);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche d\'adresses:', error);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  };

  // Fonction pour sélectionner une adresse
  const selectAddress = (suggestion) => {
    setSelectedAddress(suggestion.label);
    setAddressInput(suggestion.label);
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!commercial) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Commercial introuvable</h2>
          <button
            onClick={() => navigate('/commerciaux')}
            className="text-blue-500 hover:text-blue-700"
          >
            Retourner à la liste
          </button>
        </div>
      </div>
    );
  }

  const badge = getPerformanceBadge(commercial);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/commerciaux')}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl flex items-center justify-center">
                    <span className="text-white text-xl font-medium">
                      {commercial.prenom.charAt(0)}{commercial.nom.charAt(0)}
                    </span>
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${
                    commercial.actif ? 'bg-emerald-400' : 'bg-gray-300'
                  }`}></div>
                </div>
                
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {commercial.prenom} {commercial.nom}
                  </h1>
                  <p className="text-gray-600">{getRoleLabel(commercial.role)}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${badge.color}`}>
                      <span className="text-sm">{badge.icon}</span>
                      {badge.label}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${
                      commercial.actif 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                        : 'bg-gray-50 text-gray-600 border-gray-200'
                    }`}>
                      <div className={`w-1 h-1 rounded-full ${
                        commercial.actif ? 'bg-emerald-500' : 'bg-gray-400'
                      }`}></div>
                      {commercial.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions rapides */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleActive}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  commercial.actif
                    ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                    : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                }`}
              >
                <Power className="w-4 h-4" />
                {commercial.actif ? 'Désactiver' : 'Activer'}
              </button>
              
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
              >
                <Edit className="w-4 h-4" />
                {isEditing ? 'Annuler' : 'Modifier'}
              </button>
              
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Contenu des onglets */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'profil' && (
          <div className="space-y-6">
            {isEditing ? (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Modifier les informations</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                    <input
                      type="text"
                      value={editFormData.prenom}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, prenom: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                    <input
                      type="text"
                      value={editFormData.nom}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, nom: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                    <input
                      type="text"
                      value={editFormData.adresse || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, adresse: e.target.value }))}
                      placeholder="123 rue de la Paix, Lyon"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                    <input
                      type="text"
                      value={editFormData.code_postal || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, code_postal: e.target.value }))}
                      placeholder="69000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={editFormData.telephone}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, telephone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email personnel</label>
                    <input
                      type="email"
                      value={editFormData.email_perso || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, email_perso: e.target.value }))}
                      placeholder="jean.dupont@gmail.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email professionnel</label>
                    <input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Sauvegarder
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Informations du commercial</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  
                  {/* Informations personnelles */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      Identité
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Prénom</p>
                        <p className="font-medium text-gray-900">{commercial.prenom}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Nom</p>
                        <p className="font-medium text-gray-900">{commercial.nom}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Adresse</p>
                        <p className="font-medium text-gray-900">{commercial.adresse || 'Non renseignée'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Code postal</p>
                        <p className="font-medium text-gray-900">{commercial.code_postal || 'Non renseigné'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      Contact
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Téléphone</p>
                          <p className="font-medium text-gray-900">{commercial.telephone || 'Non renseigné'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-blue-400" />
                        <div>
                          <p className="text-sm text-gray-600">Email professionnel</p>
                          <p className="font-medium text-gray-900">{commercial.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-green-400" />
                        <div>
                          <p className="text-sm text-gray-600">Email personnel</p>
                          <p className="font-medium text-gray-900">{commercial.email_perso || 'Non renseigné'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-purple-400" />
                        <div>
                          <p className="text-sm text-gray-600">Membre depuis</p>
                          <p className="font-medium text-gray-900">
                            {new Date(commercial.created_at || Date.now()).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'statistiques' && (
          <div className="space-y-6">
            {/* Filtre de période compact avec fonctionnalités avancées */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">Période d'analyse</h4>
                <div className="flex gap-2">
                  {[
                    { key: 'jour', label: 'Jour' },
                    { key: 'semaine', label: 'Semaine' },
                    { key: 'mois', label: 'Mois' },
                    { key: 'annee', label: 'Année' }
                  ].map((period) => (
                    <button
                      key={period.key}
                      onClick={() => setFilterPeriod(period.key)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        filterPeriod === period.key
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sélecteur de date spécifique - s'affiche seulement quand un type est sélectionné */}
              {filterPeriod && (
                <div className="mt-3 pt-3 border-t border-gray-100 animate-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {filterPeriod === 'jour' && 'Sélectionnez le jour :'}
                      {filterPeriod === 'semaine' && 'Sélectionnez la semaine :'}
                      {filterPeriod === 'mois' && 'Sélectionnez le mois :'}
                      {filterPeriod === 'annee' && 'Sélectionnez l\'année :'}
                    </span>

                    <div className="flex items-center gap-2">
                      {filterPeriod === 'jour' && (
                        <input
                          type="date"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      )}

                      {filterPeriod === 'semaine' && (
                        <input
                          type="week"
                          value={selectedWeek}
                          onChange={(e) => setSelectedWeek(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      )}

                      {filterPeriod === 'mois' && (
                        <input
                          type="month"
                          value={selectedMonth}
                          onChange={(e) => setSelectedMonth(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      )}

                      {filterPeriod === 'annee' && (
                        <input
                          type="number"
                          min="2020"
                          max="2030"
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-20 transition-all"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Résumé des performances - Vue unifiée compacte */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Performance commerciale</h3>
                <span className="text-sm text-gray-500 capitalize">{currentData.label}</span>
              </div>
              
              <div className="grid grid-cols-5 gap-4">
                {/* Prospects reçus */}
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Target className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-blue-600">{currentData.reçus}</p>
                  <p className="text-xs font-medium text-gray-700">Prospects reçus</p>
                  <p className="text-xs text-green-600">{currentData.trend}</p>
                </div>

                {/* Prospects contactés */}
                <div className="text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Phone className="w-6 h-6 text-emerald-600" />
                  </div>
                  <p className="text-2xl font-bold text-emerald-600">{currentData.contactés}</p>
                  <p className="text-xs font-medium text-gray-700">Prospects contactés</p>
                  <p className="text-xs text-emerald-600">{Math.round((currentData.contactés / currentData.reçus) * 100)}%</p>
                </div>

                {/* Projets signés */}
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Award className="w-6 h-6 text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold text-purple-600">{currentData.signés}</p>
                  <p className="text-xs font-medium text-gray-700">Projets signés</p>
                  <p className="text-xs text-purple-600">{Math.round((currentData.signés / currentData.contactés) * 100)}%</p>
                </div>

                {/* Clients installés */}
                <div className="text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="w-6 h-6 text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold text-amber-600">{currentData.installés}</p>
                  <p className="text-xs font-medium text-gray-700">Clients installés</p>
                  <p className="text-xs text-amber-600">{currentData.signés > 0 ? Math.round((currentData.installés / currentData.signés) * 100) : 0}%</p>
                </div>

                {/* Taux de transformation */}
                <div className="text-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Star className="w-6 h-6 text-indigo-600" />
                  </div>
                  <p className="text-2xl font-bold text-indigo-600">{currentData.transfo}%</p>
                  <p className="text-xs font-medium text-gray-700">Taux de transfo</p>
                  <p className="text-xs text-indigo-600">Global</p>
                </div>
              </div>
            </div>

            {/* Évolution des performances sur 6 mois */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des performances (6 derniers mois)</h3>
              
              {/* En-tête des colonnes */}
              <div className="grid grid-cols-7 gap-4 mb-3 pb-2 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-700">Mois</span>
                <span className="text-sm font-medium text-blue-600 text-center">Prospects reçus</span>
                <span className="text-sm font-medium text-emerald-600 text-center">Prospects contactés</span>
                <span className="text-sm font-medium text-purple-600 text-center">Projets signés</span>
                <span className="text-sm font-medium text-amber-600 text-center">Clients installés</span>
                <span className="text-sm font-medium text-gray-700 text-center">Chiffre d'affaires</span>
                <span className="text-sm font-medium text-indigo-600 text-center">Taux transfo</span>
              </div>

              <div className="space-y-3 mb-6">
                {[
                  { mois: 'Octobre 2024', prospects: 45, contactes: 40, projets: 28, clients: 22, ca: 52100 },
                  { mois: 'Septembre 2024', prospects: 38, contactes: 34, projets: 22, clients: 18, ca: 43200 },
                  { mois: 'Août 2024', prospects: 41, contactes: 37, projets: 25, clients: 20, ca: 48900 },
                  { mois: 'Juillet 2024', prospects: 35, contactes: 30, projets: 19, clients: 15, ca: 39800 },
                  { mois: 'Juin 2024', prospects: 42, contactes: 38, projets: 24, clients: 19, ca: 46500 },
                  { mois: 'Mai 2024', prospects: 33, contactes: 28, projets: 18, clients: 14, ca: 38200 }
                ].map((data) => {
                  const tauxTransfo = Math.round((data.clients / data.prospects) * 100);
                  return (
                    <div key={data.mois} className="grid grid-cols-7 gap-4 p-3 bg-gray-50 rounded-lg items-center">
                      <span className="text-sm font-medium text-gray-700">{data.mois}</span>
                      <span className="text-sm font-bold text-blue-600 text-center">{data.prospects}</span>
                      <span className="text-sm font-bold text-emerald-600 text-center">{data.contactes}</span>
                      <span className="text-sm font-bold text-purple-600 text-center">{data.projets}</span>
                      <span className="text-sm font-bold text-amber-600 text-center">{data.clients}</span>
                      <span className="text-sm font-bold text-gray-900 text-center">{data.ca.toLocaleString()}€</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-indigo-600 w-8">{tauxTransfo}%</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${tauxTransfo}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>


            </div>

            {/* Performance énergétique */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance énergétique</h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Puissance total vendu */}
                <div className="text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Trophy className="w-6 h-6 text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold text-amber-600">8.5 kW</p>
                  <p className="text-xs font-medium text-gray-700">Puissance total vendu</p>
                  <p className="text-xs text-amber-600">2ème équipe</p>
                </div>

                {/* Surface total de panneaux vendu */}
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Target className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-blue-600">45 m²</p>
                  <p className="text-xs font-medium text-gray-700">Surface total de panneaux vendu en m²</p>
                  <p className="text-xs text-blue-600">1er équipe</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'objectifs' && (
          <div className="space-y-6">
            {/* Interface Admin - Création d'objectifs mensuels */}
            {isAdmin && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Gestion des objectifs</h3>
                  <button
                    onClick={() => setIsCreatingObjective(!isCreatingObjective)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      isCreatingObjective 
                        ? 'bg-gray-500 text-white hover:bg-gray-600' 
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      {isCreatingObjective ? 'Annuler' : 'Créer un objectif'}
                    </div>
                  </button>
                </div>

                {/* Objectifs du mois actuel */}
                {objectives.length > 0 && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-3">
                      Objectifs pour {getMonthLabel(currentMonth)} (Mois actuel)
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {objectives.map((objective) => {
                        const IconComponent = getIconComponent(objective.icon);
                        const progressPercentage = Math.min((objective.current / objective.target) * 100, 100);
                        const colorClasses = {
                          blue: 'bg-blue-50 border-blue-200 text-blue-700',
                          emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
                          purple: 'bg-purple-50 border-purple-200 text-purple-700',
                          amber: 'bg-amber-50 border-amber-200 text-amber-700',
                          red: 'bg-red-50 border-red-200 text-red-700',
                          pink: 'bg-pink-50 border-pink-200 text-pink-700',
                          indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
                          cyan: 'bg-cyan-50 border-cyan-200 text-cyan-700',
                          teal: 'bg-teal-50 border-teal-200 text-teal-700',
                          lime: 'bg-lime-50 border-lime-200 text-lime-700',
                          orange: 'bg-orange-50 border-orange-200 text-orange-700',
                          slate: 'bg-slate-50 border-slate-200 text-slate-700'
                        };

                        return (
                          <div key={objective.id} className={`p-4 rounded-lg border ${colorClasses[objective.color]}`}>
                            <div className="flex items-center justify-between mb-2">
                              {editingObjective === objective.id ? (
                                <input
                                  type="text"
                                  value={objective.label}
                                  onChange={(e) => updateObjective(objective.id, 'label', e.target.value)}
                                  className="text-sm font-medium bg-white border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                              ) : (
                                <span className="text-sm font-medium">{objective.label}</span>
                              )}
                              <div className="flex items-center gap-2">
                                <span className="text-sm">
                                  {Math.round(progressPercentage)}% atteint
                                </span>
                                <button
                                  onClick={() => setEditingObjective(editingObjective === objective.id ? null : objective.id)}
                                  className="text-blue-500 hover:text-blue-700"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => deleteObjective(objective.id)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {editingObjective === objective.id ? (
                                  <div className="flex gap-2">
                                    <input
                                      type="number"
                                      value={objective.current}
                                      onChange={(e) => updateObjective(objective.id, 'current', parseInt(e.target.value))}
                                      className="w-16 px-2 py-1 text-sm bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                      placeholder="Réalisé"
                                    />
                                    <span className="text-sm self-center">/</span>
                                    <input
                                      type="number"
                                      value={objective.target}
                                      onChange={(e) => updateObjective(objective.id, 'target', parseInt(e.target.value))}
                                      className="w-16 px-2 py-1 text-sm bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                      placeholder="Objectif"
                                    />
                                  </div>
                                ) : (
                                  <span className="text-lg font-bold">
                                    {objective.current.toLocaleString()} / {objective.target.toLocaleString()}
                                    {objective.type === 'conversion' ? '%' : ''}
                                  </span>
                                )}
                                <IconComponent className="w-5 h-5" />
                              </div>
                            </div>
                            <div className={`w-full bg-gray-200 rounded-full h-2`}>
                              <div 
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  objective.color === 'blue' ? 'bg-blue-500' :
                                  objective.color === 'emerald' ? 'bg-emerald-500' :
                                  objective.color === 'purple' ? 'bg-purple-500' :
                                  objective.color === 'amber' ? 'bg-amber-500' :
                                  objective.color === 'red' ? 'bg-red-500' :
                                  objective.color === 'pink' ? 'bg-pink-500' :
                                  objective.color === 'indigo' ? 'bg-indigo-500' :
                                  objective.color === 'cyan' ? 'bg-cyan-500' :
                                  objective.color === 'teal' ? 'bg-teal-500' :
                                  objective.color === 'lime' ? 'bg-lime-500' :
                                  objective.color === 'orange' ? 'bg-orange-500' :
                                  objective.color === 'slate' ? 'bg-slate-500' :
                                  'bg-blue-500'
                                }`}
                                style={{ width: `${progressPercentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Message si pas d'objectifs */}
                {objectives.length === 0 && (
                  <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <div className="text-gray-500 mb-2">
                      <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-lg font-medium">Aucun objectif défini</p>
                      <p className="text-sm">Créez votre premier objectif pour {getMonthLabel(currentMonth)}</p>
                    </div>
                  </div>
                )}

                {/* Formulaire de création d'objectif avec sélection de mois */}
                {isCreatingObjective && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 mb-6 border border-green-200">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <Plus className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">Créer un nouvel objectif</h4>
                    </div>
                    
                    <div className="grid gap-6">
                      {/* Première ligne - Mois et Type */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-green-500" />
                              Mois de l'objectif
                            </div>
                          </label>
                          <select
                            value={newObjective.month}
                            onChange={(e) => setNewObjective({...newObjective, month: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                          >
                            <option value="">📅 Sélectionner un mois</option>
                            {Object.keys(monthlyObjectives).sort().map(monthKey => (
                              <option key={monthKey} value={monthKey}>
                                {getMonthLabel(monthKey)}
                                {monthKey === currentMonth ? ' (Mois actuel)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <div className="flex items-center gap-2">
                              <BarChart3 className="w-4 h-4 text-green-500" />
                              Type d'objectif
                            </div>
                          </label>
                          <select
                            value={newObjective.type}
                            onChange={(e) => {
                              const selectedType = e.target.value;
                              let label = '';
                              switch(selectedType) {
                                case 'prospects':
                                  label = 'Prospects contactés';
                                  break;
                                case 'projects':
                                  label = 'Projets signés';
                                  break;
                                case 'installations':
                                  label = 'Clients installés';
                                  break;
                                case 'conversion':
                                  label = 'Taux de transfo';
                                  break;
                              }
                              setNewObjective({...newObjective, type: selectedType, label: label});
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                          >
                            <option value="">🎯 Sélectionner un type</option>
                            <option value="prospects">👥 Prospects contactés</option>
                            <option value="projects">📋 Projets signés</option>
                            <option value="installations">⚡ Clients installés</option>
                            <option value="conversion">📈 Taux de transfo</option>
                          </select>
                        </div>
                      </div>

                      {/* Deuxième ligne - Label et Valeur cible */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <div className="flex items-center gap-2">
                              <Target className="w-4 h-4 text-green-500" />
                              Nom de l'objectif
                            </div>
                          </label>
                          <input
                            type="text"
                            value={newObjective.label}
                            onChange={(e) => setNewObjective({...newObjective, label: e.target.value})}
                            placeholder="Le nom se remplit automatiquement"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 shadow-sm transition-all duration-200 hover:border-gray-400 bg-gray-50"
                            readOnly
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-green-500" />
                              Valeur cible
                            </div>
                          </label>
                          <input
                            type="number"
                            value={newObjective.target}
                            onChange={(e) => setNewObjective({...newObjective, target: e.target.value})}
                            placeholder="Ex: 150"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 shadow-sm transition-all duration-200 hover:border-gray-400"
                          />
                        </div>
                      </div>

                      {/* Troisième ligne - Couleur et Icône */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <div className="flex items-center gap-2">
                              <Star className="w-4 h-4 text-green-500" />
                              Couleur thématique
                            </div>
                          </label>
                          <select
                            value={newObjective.color}
                            onChange={(e) => setNewObjective({...newObjective, color: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                          >
                            <option value="blue">🔵 Bleu</option>
                            <option value="emerald">🟢 Emeraude</option>
                            <option value="purple">🟣 Violet</option>
                            <option value="amber">🟡 Ambre</option>
                            <option value="red">🔴 Rouge</option>
                            <option value="pink">🩷 Rose</option>
                            <option value="indigo">🟦 Indigo</option>
                            <option value="cyan">🩵 Cyan</option>
                            <option value="teal">🟫 Sarcelle</option>
                            <option value="lime">🟢 Lime</option>
                            <option value="orange">🟠 Orange</option>
                            <option value="slate">⚫ Ardoise</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <div className="flex items-center gap-2">
                              <Award className="w-4 h-4 text-green-500" />
                              Icône
                            </div>
                          </label>
                          <select
                            value={newObjective.icon}
                            onChange={(e) => setNewObjective({...newObjective, icon: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                          >
                            <option value="Target">🎯 Target</option>
                            <option value="Phone">📞 Téléphone</option>
                            <option value="DollarSign">💰 Argent</option>
                            <option value="Award">🏆 Récompense</option>
                            <option value="Trophy">🥇 Trophée</option>
                            <option value="TrendingUp">📈 Tendance</option>
                            <option value="BarChart3">📊 Graphique</option>
                            <option value="Star">⭐ Étoile</option>
                            <option value="Clock">⏰ Horloge</option>
                            <option value="Plus">➕ Plus</option>
                          </select>
                        </div>
                      </div>

                      {/* Boutons d'action */}
                      <div className="flex justify-between items-center pt-4 border-t border-green-200">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          Remplissez tous les champs pour créer l'objectif
                        </div>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setIsCreatingObjective(false);
                              setNewObjective({
                                type: '',
                                label: '',
                                target: '',
                                color: 'blue',
                                icon: 'Target',
                                month: ''
                              });
                            }}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                          >
                            <X className="w-4 h-4 inline mr-2" />
                            Terminer
                          </button>
                          <button
                            onClick={addObjective}
                            disabled={!newObjective.type || !newObjective.label || !newObjective.target || !newObjective.month}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                          >
                            <Plus className="w-4 h-4 inline mr-2" />
                            Créer l'objectif
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Historique des performances - 6 derniers mois */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des performances (6 derniers mois)</h3>
              <div className="space-y-3">
                {performanceHistory.map((month) => {
                  const status = getMonthStatus(month);
                  return (
                    <div key={month.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-medium text-gray-700 w-28">{month.mois}</span>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">
                            Prospects: <span className="font-medium">{month.prospects.realise}</span> / {month.prospects.objectif}
                          </span>
                          <span className="text-gray-600">
                            Projets: <span className="font-medium">{month.projects.realise}</span> / {month.projects.objectif}
                          </span>
                          <span className="text-gray-600">
                            Installs: <span className="font-medium">{month.installations.realise}</span> / {month.installations.objectif}
                          </span>
                          <span className="text-gray-600">
                            Taux: <span className="font-medium">{month.conversion.realise}%</span> / {month.conversion.objectif}%
                          </span>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        status.color === 'emerald' ? 'bg-emerald-100 text-emerald-800' :
                        status.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                        status.color === 'amber' ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {status.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="space-y-6">
            {/* Badge et Score actuel */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Badge de Performance</h3>
                <div className="text-right">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-lg font-medium border ${getPerformanceBadge(commercial).color}`}>
                    <span className="text-2xl">{getPerformanceBadge(commercial).icon}</span>
                    {getPerformanceBadge(commercial).label}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Badge actuel</p>
                </div>
              </div>

              {/* Progression vers le badge suivant */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Progression vers Or</span>
                  <span className="text-sm text-gray-600">85% / 90%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 h-3 rounded-full" style={{ width: '94%' }}></div>
                </div>
                <p className="text-xs text-gray-600 mt-1">Il vous manque 5 points pour atteindre le badge Or</p>
              </div>
            </div>

            {/* Détail des critères de performance */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Critères de Performance</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium text-blue-700">Prospection</span>
                      </div>
                      <span className="text-lg font-bold text-blue-900">92/100</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">42 prospects ce mois (objectif: 40)</p>
                  </div>

                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm font-medium text-emerald-700">Chiffre d'affaires</span>
                      </div>
                      <span className="text-lg font-bold text-emerald-900">95/100</span>
                    </div>
                    <div className="w-full bg-emerald-200 rounded-full h-2">
                      <div className="bg-emerald-500 h-2 rounded-full" style={{ width: '95%' }}></div>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">47 350€ ce mois (objectif: 44 000€)</p>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-purple-500" />
                        <span className="text-sm font-medium text-purple-700">Taux conversion</span>
                      </div>
                      <span className="text-lg font-bold text-purple-900">85/100</span>
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-2">
                      <div className="bg-purple-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                    </div>
                    <p className="text-xs text-purple-600 mt-1">54% de conversion (23/42 prospects)</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-medium text-amber-700">Régularité</span>
                      </div>
                      <span className="text-lg font-bold text-amber-900">78/100</span>
                    </div>
                    <div className="w-full bg-amber-200 rounded-full h-2">
                      <div className="bg-amber-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                    </div>
                    <p className="text-xs text-amber-600 mt-1">Présence et suivi clients</p>
                  </div>

                  <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-rose-500" />
                        <span className="text-sm font-medium text-rose-700">Satisfaction client</span>
                      </div>
                      <span className="text-lg font-bold text-rose-900">88/100</span>
                    </div>
                    <div className="w-full bg-rose-200 rounded-full h-2">
                      <div className="bg-rose-500 h-2 rounded-full" style={{ width: '88%' }}></div>
                    </div>
                    <p className="text-xs text-rose-600 mt-1">Note moyenne: 4.4/5 sur 15 avis</p>
                  </div>

                  <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-indigo-500" />
                        <span className="text-sm font-medium text-indigo-700">Évolution</span>
                      </div>
                      <span className="text-lg font-bold text-indigo-900">82/100</span>
                    </div>
                    <div className="w-full bg-indigo-200 rounded-full h-2">
                      <div className="bg-indigo-500 h-2 rounded-full" style={{ width: '82%' }}></div>
                    </div>
                    <p className="text-xs text-indigo-600 mt-1">+8% vs mois précédent</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Historique des badges */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des badges</h3>
              <div className="flex items-center gap-4 flex-wrap">
                {[
                  { mois: 'Nov 2024', badge: '💎', label: 'Diamant', score: 85, actuel: true },
                  { mois: 'Oct 2024', badge: '🥇', label: 'Or', score: 92, actuel: false },
                  { mois: 'Sep 2024', badge: '🥇', label: 'Or', score: 85, actuel: false },
                  { mois: 'Aoû 2024', badge: '🥇', label: 'Or', score: 88, actuel: false },
                  { mois: 'Jul 2024', badge: '🥈', label: 'Argent', score: 78, actuel: false },
                  { mois: 'Jun 2024', badge: '🥇', label: 'Or', score: 86, actuel: false }
                ].map((data) => (
                  <div key={data.mois} className={`p-3 rounded-lg border ${data.actuel ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="text-center">
                      <span className="text-2xl">{data.badge}</span>
                      <p className="text-xs font-medium text-gray-700 mt-1">{data.mois}</p>
                      <p className="text-xs text-gray-600">{data.score}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Conseils d'amélioration */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Conseils pour améliorer vos performances</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Target className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900">Augmentez votre régularité</h4>
                    <p className="text-sm text-blue-700">Maintenez un suivi constant avec vos prospects pour améliorer votre score de régularité</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <TrendingUp className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-emerald-900">Continuez sur cette lancée</h4>
                    <p className="text-sm text-emerald-700">Vos performances CA sont excellentes, maintenez cet effort pour atteindre Diamant</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'planning' && (
          <div className="space-y-6">
            {/* Agenda Google Calendar Dark Theme */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
              {/* Header sombre de l'agenda */}
              <div className="border-b border-gray-700 px-6 py-4 bg-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">Agenda</h3>
                    </div>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => navigateMonth(-1)}
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <div className="text-lg font-medium text-white min-w-[180px] text-center">
                        {formatMonthYear(agendaDate)}
                      </div>
                      <button 
                        onClick={() => navigateMonth(1)}
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={goToToday}
                      className="px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-700 border border-gray-600 rounded-lg transition-colors"
                    >
                      Aujourd'hui
                    </button>
                    <div className="flex bg-gray-700 rounded-lg p-1">
                      <button 
                        onClick={() => switchView('semaine')}
                        className={`px-3 py-1 text-sm rounded transition-colors ${
                          agendaView === 'semaine' 
                            ? 'bg-blue-600 text-white shadow-sm font-medium' 
                            : 'text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Semaine
                      </button>
                      <button 
                        onClick={() => switchView('mois')}
                        className={`px-3 py-1 text-sm rounded transition-colors ${
                          agendaView === 'mois' 
                            ? 'bg-blue-600 text-white shadow-sm font-medium' 
                            : 'text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Mois
                      </button>
                    </div>
                    <button 
                      onClick={() => setShowEventModal(true)}
                      className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Créer
                    </button>
                  </div>
                </div>
              </div>

              {/* Structure principale avec sidebar */}
              <div className="flex bg-gray-900">
                {/* Sidebar gauche */}
                <div className="w-64 bg-gray-800 border-r border-gray-700 p-4">
                  {/* Menu principal */}
                  <div className="mb-6">
                    <button className="flex items-center gap-2 w-full p-2 text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors">
                      <Plus className="w-4 h-4" />
                      <span className="text-sm font-medium">Créer</span>
                    </button>
                  </div>

                  {/* Mini calendrier */}
                  <div className="mb-6">
                    <div className="text-white text-sm font-medium mb-3">{formatMonthYear(agendaDate)}</div>
                    <div className="grid grid-cols-7 gap-1 text-xs">
                      {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((day, i) => (
                        <div key={i} className="text-center text-gray-400 py-1">{day}</div>
                      ))}
                      {Array.from({ length: 35 }, (_, i) => {
                        const date = new Date(agendaDate.getFullYear(), agendaDate.getMonth(), i - 6);
                        const isCurrentMonth = date.getMonth() === agendaDate.getMonth();
                        const isToday = date.toDateString() === new Date().toDateString();
                        return (
                          <div
                            key={i}
                            className={`text-center py-1 cursor-pointer rounded transition-colors ${
                              isToday 
                                ? 'bg-blue-600 text-white' 
                                : isCurrentMonth 
                                ? 'text-gray-300 hover:bg-gray-700' 
                                : 'text-gray-600'
                            }`}
                          >
                            {date.getDate()}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Mes agendas */}
                  <div>
                    <h4 className="text-white text-sm font-medium mb-3">Mes agendas</h4>
                    <div className="space-y-2">
                      {[
                        { name: 'Eco Éner', color: 'bg-green-500', checked: true },
                        { name: 'Anniversaires', color: 'bg-purple-500', checked: true },
                        { name: 'Orga', color: 'bg-red-500', checked: false },
                        { name: 'Rappel', color: 'bg-blue-500', checked: true },
                        { name: 'Shell (Anthony )', color: 'bg-orange-500', checked: false },
                        { name: 'Tâches', color: 'bg-yellow-500', checked: true },
                        { name: 'Yahdiel (Romain )', color: 'bg-pink-500', checked: false }
                      ].map((agenda, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={agenda.checked}
                            className="w-3 h-3 rounded"
                            onChange={() => {}}
                          />
                          <div className={`w-3 h-3 ${agenda.color} rounded`}></div>
                          <span className="text-gray-300 text-xs">{agenda.name}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4">
                      <h4 className="text-white text-sm font-medium mb-3">Autres agendas</h4>
                      <div className="space-y-2">
                        {[
                          { name: 'VOEL Équipe 1', checked: true },
                          { name: 'VOEL Équipe 2', checked: true },
                          { name: 'VOEL - ECO ENER', checked: true },
                          { name: 'VOEL Équipe 3', checked: true }
                        ].map((agenda, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={agenda.checked}
                              className="w-3 h-3 rounded"
                              onChange={() => {}}
                            />
                            <span className="text-gray-300 text-xs">{agenda.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vue calendrier principale */}
                <div className="flex-1 overflow-hidden">
                  {/* En-têtes des jours - Lundi à Vendredi seulement */}
                  <div className="flex border-b border-gray-700 bg-gray-800">
                    <div className="w-16 p-2 text-center text-xs font-medium text-gray-400 border-r border-gray-700">
                      GMT+1
                    </div>
                    <div className="flex-1 grid grid-cols-5">
                      {getWeekDays().map((date, index) => (
                        <div key={`day-header-${date.toISOString()}-${index}`} className="p-3 text-center border-r border-gray-700 last:border-r-0">
                          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                            {['LUN', 'MAR', 'MER', 'JEU', 'VEN'][index]}
                          </div>
                          <div className={`mt-1 text-2xl font-normal ${
                            isToday(date) 
                              ? 'w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto text-sm font-medium' 
                              : 'text-gray-200'
                          }`}>
                            {date.getDate()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Grille horaire étendue - Lundi à Vendredi */}
                  <div className="flex max-h-[600px] overflow-y-auto bg-gray-900">
                    {/* Colonne des heures - Réduite de moitié */}
                    <div className="w-16 border-r border-gray-700 bg-gray-800">
                      {Array.from({ length: 20 }, (_, i) => (
                        <div key={i} className="h-12 border-b border-gray-700 flex items-start justify-end pr-1 py-1">
                          <span className="text-xs text-gray-400">
                            {String(4 + i).padStart(2, '0')}:00
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Colonnes des jours - Flex pour occuper l'espace restant */}
                    <div className="flex-1 grid grid-cols-5">
                      {Array.from({ length: 5 }, (_, dayIndex) => (
                        <div key={`day-column-${dayIndex}`} className="border-r border-gray-700 last:border-r-0 relative">
                          {Array.from({ length: 20 }, (_, hourIndex) => (
                            <div 
                              key={`hour-slot-${dayIndex}-${hourIndex}`} 
                              className="h-12 border-b border-gray-700 hover:bg-gray-800 cursor-pointer transition-colors relative group"
                              onClick={() => handleTimeSlotClick(dayIndex, hourIndex)}
                            >
                              {/* Événements avec style jaune/orange */}
                              <DarkEventSlot 
                                dayIndex={dayIndex} 
                                hourIndex={hourIndex} 
                                event={getDarkEventForTimeSlot(dayIndex, hourIndex)} 
                              />

                              {/* Indicateur pour créer un événement */}
                              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="absolute top-1 left-1 w-2 h-2 bg-blue-400 rounded-full"></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Prochains rendez-vous */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Prochains rendez-vous</h3>
              <div className="space-y-3">
                {[
                  { 
                    date: 'Aujourd\'hui 14h00', 
                    client: 'Mme Dubois', 
                    type: 'Appel téléphonique', 
                    adresse: 'Tel: 06 12 34 56 78',
                    statut: 'confirmed',
                    notes: 'Rappel devis panneaux photovoltaïques'
                  },
                  { 
                    date: 'Aujourd\'hui 16h00', 
                    client: 'SAS TechSol', 
                    type: 'Visite technique', 
                    adresse: '15 rue de la Paix, 69000 Lyon',
                    statut: 'confirmed',
                    notes: 'Étude de faisabilité installation industrielle'
                  },
                  { 
                    date: 'Demain 9h30', 
                    client: 'Famille Leroy', 
                    type: 'Présentation devis', 
                    adresse: '45 avenue des Chênes, 69100 Villeurbanne',
                    statut: 'pending',
                    notes: 'Première présentation - installation résidentielle'
                  },
                  { 
                    date: 'Demain 15h00', 
                    client: 'M. Petit', 
                    type: 'Suivi client', 
                    adresse: 'Tel: 07 98 76 54 32',
                    statut: 'confirmed',
                    notes: 'Point sur avancement dossier financement'
                  }
                ].map((rdv, index) => (
                  <div key={`rdv-${index}-${rdv.client}-${rdv.date}`} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className={`w-3 h-3 rounded-full mt-2 ${rdv.statut === 'confirmed' ? 'bg-emerald-400' : 'bg-amber-400'}`}></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-gray-900">{rdv.client}</h4>
                        <span className="text-sm text-gray-600">{rdv.date}</span>
                      </div>
                      <p className="text-sm text-blue-600 mb-1">{rdv.type}</p>
                      <p className="text-sm text-gray-600 mb-1">{rdv.adresse}</p>
                      <p className="text-xs text-gray-500">{rdv.notes}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-1 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded">
                        <Phone className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Statistiques du planning */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">RDV cette semaine</p>
                    <p className="text-2xl font-bold text-blue-600">7</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Taux présence</p>
                    <p className="text-2xl font-bold text-emerald-600">94%</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Conversion RDV</p>
                    <p className="text-2xl font-bold text-purple-600">67%</p>
                  </div>
                </div>
              </div>
            </div>


          </div>
        )}

        {activeTab === 'parametres' && (
          <div className="space-y-6">
            {/* Configuration générale */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration du profil</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secteur géographique</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                      <option>Lyon et périphérie</option>
                      <option>Rhône-Alpes Nord</option>
                      <option>Rhône-Alpes Sud</option>
                      <option>National</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Spécialité</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                      <option>Installation résidentielle</option>
                      <option>Installation industrielle</option>
                      <option>Toutes installations</option>
                      <option>Maintenance et SAV</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Commission (%)</label>
                    <input
                      type="number"
                      defaultValue="8.5"
                      step="0.1"
                      min="0"
                      max="15"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Objectif mensuel (€)</label>
                    <input
                      type="number"
                      defaultValue="44000"
                      step="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Manager responsable</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                      <option>M. Dupont (Manager Vente)</option>
                      <option>Mme Martin (Manager Régional)</option>
                      <option>M. Bernard (Directeur Commercial)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Équipe</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                      <option>Équipe Lyon Centre</option>
                      <option>Équipe Rhône Nord</option>
                      <option>Équipe Rhône Sud</option>
                      <option>Équipe Nationale</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Préférences de notifications */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Notifications et alertes</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Nouveaux prospects</p>
                    <p className="text-sm text-gray-600">Notification lors de l'attribution de prospects</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Rappels de RDV</p>
                    <p className="text-sm text-gray-600">Alerte 30 minutes avant les rendez-vous</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Objectifs mensuels</p>
                    <p className="text-sm text-gray-600">Rapport de progression hebdomadaire</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Félicitations</p>
                    <p className="text-sm text-gray-600">Notification pour les réussites et badges</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
                </div>
              </div>
            </div>

            {/* Accès et permissions */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Accès et permissions</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Modules accessibles</h4>
                  <div className="space-y-2">
                    {[
                      { module: 'Dashboard Vente', actif: true },
                      { module: 'Gestion Clients', actif: true },
                      { module: 'Devis & Factures', actif: true },
                      { module: 'Planning', actif: true },
                      { module: 'Challenges', actif: true },
                      { module: 'Chat Équipe', actif: true },
                      { module: 'Boîte à idées', actif: false },
                      { module: 'Administration', actif: false }
                    ].map((item) => (
                      <div key={item.module} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className={`text-sm ${item.actif ? 'text-gray-900' : 'text-gray-500'}`}>
                          {item.module}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          item.actif ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {item.actif ? 'Activé' : 'Désactivé'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Dernières connexions</h4>
                  <div className="space-y-2">
                    {[
                      { date: 'Aujourd\'hui 08:45', ip: '192.168.1.100', device: 'Chrome - Windows' },
                      { date: 'Hier 17:30', ip: '192.168.1.100', device: 'Chrome - Windows' },
                      { date: '03/11/2024 09:15', ip: '10.0.0.50', device: 'Safari - iPhone' },
                      { date: '02/11/2024 14:20', ip: '192.168.1.100', device: 'Chrome - Windows' }
                    ].map((conn) => (
                      <div key={`${conn.date}-${conn.ip}`} className="p-2 bg-gray-50 rounded text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{conn.date}</span>
                          <span className="text-gray-600">{conn.ip}</span>
                        </div>
                        <span className="text-gray-600">{conn.device}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions sensibles */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions du compte</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <button className="flex items-center gap-2 p-3 text-left bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                  <Edit className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium text-blue-900">Modifier mot de passe</p>
                    <p className="text-sm text-blue-600">Changer le mot de passe de connexion</p>
                  </div>
                </button>

                <button className="flex items-center gap-2 p-3 text-left bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors">
                  <Power className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="font-medium text-amber-900">Suspendre le compte</p>
                    <p className="text-sm text-amber-600">Désactiver temporairement l'accès</p>
                  </div>
                </button>

                <button className="flex items-center gap-2 p-3 text-left bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors">
                  <Save className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="font-medium text-emerald-900">Exporter données</p>
                    <p className="text-sm text-emerald-600">Télécharger un rapport complet</p>
                  </div>
                </button>

                <button className="flex items-center gap-2 p-3 text-left bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                  <Trash2 className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="font-medium text-red-900">Supprimer le compte</p>
                    <p className="text-sm text-red-600">Action irréversible</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Bouton de sauvegarde */}
            <div className="flex justify-end">
              <button className="flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                <Save className="w-4 h-4" />
                Sauvegarder les paramètres
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de création d'événement - Style Google Calendar Dark */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden">
            {/* Header du modal */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <input
                type="text"
                placeholder="Ajouter un titre"
                className="bg-transparent text-white text-lg font-normal placeholder-gray-400 outline-none flex-1"
              />
              <button
                onClick={() => {
                  setShowEventModal(false);
                  setSelectedTimeSlot(null);
                }}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors ml-4"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Onglets */}
            <div className="px-4 pt-4">
              <div className="flex gap-1">
                <button key="event-tab" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-full">
                  Événement
                </button>
                <button key="task-tab" className="px-4 py-2 text-gray-400 text-sm hover:bg-gray-700 rounded-full transition-colors">
                  Tâche
                </button>
                <button key="appointment-tab" className="px-4 py-2 text-gray-400 text-sm hover:bg-gray-700 rounded-full transition-colors">
                  Planning des rendez-vous
                </button>
                <span key="new-badge" className="px-3 py-1 bg-blue-600 text-white text-xs rounded-full ml-2">
                  Nouveauté
                </span>
              </div>
            </div>

            {/* Contenu du modal */}
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Section date et heure */}
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-white">
                    {selectedTimeSlot ? (
                      <>
                        {selectedTimeSlot.date?.toLocaleDateString('fr-FR', { 
                          weekday: 'long', 
                          day: 'numeric',
                          month: 'long' 
                        })} {selectedTimeSlot.hour}:00 - {selectedTimeSlot.hour + 1}:00
                      </>
                    ) : (
                      'Mercredi, 10 septembre  22:00 - 23:00'
                    )}
                  </div>
                  <div className="text-gray-400 text-sm">
                    Fuseau horaire • Une seule fois
                  </div>
                </div>
              </div>

              {/* Sections du modal avec clés React */}
              <div key="guests-section" className="flex items-center gap-3 py-3 hover:bg-gray-700 rounded-lg px-2 cursor-pointer transition-colors">
                <div className="w-5 h-5 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400">
                    <path fill="currentColor" d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2 1l-3 4v7h3v-6h1.5l1.5 4.5V22h3z"/>
                  </svg>
                </div>
                <span className="text-gray-300">Ajouter des invités</span>
              </div>

              <div key="meet-section" className="flex items-center gap-3 py-3 hover:bg-gray-700 rounded-lg px-2 cursor-pointer transition-colors">
                <div className="w-5 h-5 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-400">
                    <path fill="currentColor" d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z"/>
                  </svg>
                </div>
                <span className="text-gray-300">Ajouter une visioconférence Google Meet</span>
              </div>

              {/* Section lieu avec autocomplétion */}
              <div key="location-section" className="py-3">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400">
                      <path fill="currentColor" d="M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22S19,14.25 19,9A7,7 0 0,0 12,2M12,4A5,5 0 0,1 17,9C17,11.88 12,18.21 12,18.21S7,11.88 7,9A5,5 0 0,1 12,4M12,7A2,2 0 0,0 10,9A2,2 0 0,0 12,11A2,2 0 0,0 14,9A2,2 0 0,0 12,7Z"/>
                    </svg>
                  </div>
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Ajouter une adresse..."
                      value={addressInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setAddressInput(value);
                        searchAddresses(value);
                      }}
                      className="w-full bg-transparent text-gray-300 placeholder-gray-500 border-b border-gray-600 focus:border-blue-400 outline-none py-1"
                    />
                    
                    {/* Liste des suggestions */}
                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-gray-700 border border-gray-600 rounded-lg mt-1 max-h-48 overflow-y-auto z-10">
                        {addressSuggestions.map((suggestion, index) => (
                          <div
                            key={`address-${suggestion.properties.label}-${index}`}
                            onClick={() => selectAddress(suggestion)}
                            className="p-3 hover:bg-gray-600 cursor-pointer transition-colors border-b border-gray-600 last:border-b-0"
                          >
                            <div className="text-white text-sm font-medium">
                              {suggestion.address}
                            </div>
                            <div className="text-gray-400 text-xs mt-1">
                              {suggestion.city} • {suggestion.postcode}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div key="description-section" className="flex items-center gap-3 py-3 hover:bg-gray-700 rounded-lg px-2 cursor-pointer transition-colors">
                <div className="w-5 h-5 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400">
                    <path fill="currentColor" d="M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
                  </svg>
                </div>
                <span className="text-gray-300">Ajouter description ou une pièce jointe Google Drive</span>
              </div>

              {/* Équipe */}
              <div className="flex items-center gap-3 py-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div className="flex items-center gap-2">
                  <span className="text-white">Équipe 1</span>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
              </div>
              <div className="text-gray-400 text-sm pl-8">
                Occupé • Visibilité par défaut • Ne pas envoyer de notification
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-700 bg-gray-800">
              <button className="text-blue-400 hover:text-blue-300 transition-colors">
                Autres options
              </button>
              <button
                onClick={() => {
                  // Pour l'instant, créer un événement de test
                  createNewEvent("Nouvel événement", 'visite');
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommercialProfile;